// src/components/AddBlog.tsx
import React, { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import Gallery from "../components/Gallery";
import "../CSS/App.css";
import { DocumentData } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { Cloudinary } from "@cloudinary/url-gen";
import axios from "axios";
import Resizer from "react-image-file-resizer";

interface BlogPost extends DocumentData {
  id: string;
  title: string;
  text: string;
  imageUrl?: string;
  galleryImages: string[];
  youtubeUrl?: string;
  showInHome?: boolean;
  createdAt: number;
  isSliding?: boolean;
}

const AddBlog: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [isSliding, setIsSliding] = useState(false);
  const [showInHome, setShowInHome] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const galleryInputRef = React.useRef<HTMLInputElement>(null);
  const toast = React.useRef<any>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleGalleryUploadClick = () => {
    galleryInputRef.current?.click();
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    if (id) {
      const fetchPost = async () => {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/posts/${id}`
        );
        const postData = response.data as BlogPost;
        setTitle(postData.title);
        setText(postData.text);
        setImageUrl(postData.imageUrl || "");
        setGalleryFiles([]);
        setYoutubeUrl(postData.youtubeUrl || "");
        setIsSliding(Boolean(postData.isSliding));
        setShowInHome(Boolean(postData.showInHome));
        setSelectedPostId(id);
        setEditMode(true);
      };
      fetchPost();
    }
  }, [id]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
    }
  };

  const validateImage = (file: File): boolean => {
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB
    const ALLOWED_TYPES = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
    ];

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Please upload only JPG, PNG, GIF, or WebP images");
      return false;
    }

    if (file.size > MAX_SIZE) {
      setError("Image size should be less than 5MB");
      return false;
    }

    return true;
  };

  const validateYoutubeUrl = (url: string) => {
    if (!url) return true; // Empty URL is valid (optional field)
    const patterns = [
      /^https?:\/\/(?:www\.)?youtube\.com\/watch\?v=[^&\s]+/,
      /^https?:\/\/(?:www\.)?youtu\.be\/[^&\s]+/,
      /^https?:\/\/(?:www\.)?youtube\.com\/embed\/[^&\s]+/,
    ];
    return patterns.some((pattern) => pattern.test(url));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    const file = e.target.files?.[0];
    if (!file) return;

    if (!validateImage(file)) return;

    // Resize and compress the image
    Resizer.imageFileResizer(
      file,
      1024, // width
      1024, // height
      "JPEG",
      70, // quality
      0, // rotation
      (uri) => {
        // Convert base64 to Blob
        fetch(uri as string)
          .then(res => res.blob())
          .then(blob => {
            // Check if the resized image is under 300KB
            if (blob.size > 300 * 1024) {
              setError("Image size is still over 300KB after compression. Please try a smaller image.");
              return;
            }
            
            // Convert Blob to File
            const resizedFile = new File([blob], file.name, { type: "image/jpeg" });
            setImageFile(resizedFile);
            setImageUrl(URL.createObjectURL(resizedFile));
          });
      },
      "base64"
    );
  };

  const handleGalleryImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;
    if (!files) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      if (validateImage(files[i])) {
        validFiles.push(files[i]);
      }
    }

    setGalleryFiles(validFiles);
  };

  const handleYoutubeUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setYoutubeUrl(url);

    if (url && !validateYoutubeUrl(url)) {
      setError(
        "Please enter a valid YouTube URL (e.g., https://www.youtube.com/watch?v=...)"
      );
    } else {
      setError("");
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "ml_default");
      formData.append("cloud_name", "dzuppkxqa");

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/dzuppkxqa/image/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to upload image");
      }

      const data = await response.json();
      return data.secure_url;
    } catch (error) {
      console.error("Error uploading image:", error);
      setError("Failed to upload image. Please try again.");
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      let uploadedImageUrl = imageUrl;
      let uploadedGalleryUrls: string[] = [];

      if (imageFile) {
        uploadedImageUrl = await uploadImage(imageFile);
      }

      // Upload all gallery images
      if (galleryFiles.length > 0) {
        const uploadPromises = galleryFiles.map((file) => uploadImage(file));
        uploadedGalleryUrls = await Promise.all(uploadPromises);
      }

      const postData: BlogPost = {
        id: selectedPostId || uuidv4(),
        title,
        text,
        imageUrl: uploadedImageUrl || "",
        galleryImages: uploadedGalleryUrls,
        youtubeUrl: youtubeUrl.trim(),
        showInHome: Boolean(showInHome),
        createdAt: Date.now(),
        isSliding: Boolean(isSliding),
      };

      if (editMode && selectedPostId) {
        // const postRef = doc(db, "posts", selectedPostId);
        // await updateDoc(postRef, postData);
        await axios.put(
          `${process.env.REACT_APP_API}/posts/${selectedPostId}`,
          postData
        );
      } else {
        // const postsRef = collection(db, "posts");
        // await addDoc(postsRef, postData);
        await axios.post(`${process.env.REACT_APP_API}/posts`, postData);
      }

      setLoading(false);
      navigate("/blog");
    } catch (error) {
      console.error("Error saving post:", error);
      setLoading(false);
    }
  };

  const handleDelete = async (postId: string) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API}/posts/${postId}`);
      await fetchPosts();
      navigate("/");
    } catch (error) {
      console.error("Error deleting post:", error);
    }
  };

  return (
    <div className="add-ad-container">
      <Card
        title={editMode ? "Edit Blog Post" : "Create New Blog Post"}
        className="add-ad-card"
      >
        <Toast ref={toast} />
        <form onSubmit={handleSubmit} className="p-fluid">
          <div className="p-field mb-3">
            <label htmlFor="title">Title</label>
            <InputText
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="p-field mb-3">
            <label htmlFor="text">Content</label>
            <InputTextarea
              id="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              required
              className="whitespace-pre-wrap w-full"
            />
          </div>

          <div className="p-field mb-3">
            <label htmlFor="image">Main Image</label>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleImageChange}
              accept="image/*"
            />
            <Button
              type="button"
              label="Upload Image"
              icon="pi pi-upload"
              onClick={handleUploadClick}
              className="p-button-secondary"
            />
            {imageUrl && (
              <div className="preview-image mt-2">
                <img
                  src={imageUrl}
                  alt="Preview"
                  style={{ maxWidth: "200px" }}
                />
              </div>
            )}
          </div>

          <div className="p-field mb-3">
            <label htmlFor="gallery">Gallery Images</label>
            <input
              type="file"
              ref={galleryInputRef}
              style={{ display: "none" }}
              onChange={handleGalleryImageChange}
              accept="image/*"
              multiple
            />
            <Button
              type="button"
              label="Upload Gallery Images"
              icon="pi pi-images"
              onClick={handleGalleryUploadClick}
              className="p-button-secondary"
            />
            {galleryFiles.length > 0 && (
              <Gallery
                images={galleryFiles.map((file) => URL.createObjectURL(file))}
                isEditing={true}
                onImageRemove={(index) => {
                  const newGalleryFiles = [...galleryFiles];
                  newGalleryFiles.splice(index, 1);
                  setGalleryFiles(newGalleryFiles);
                }}
              />
            )}
          </div>

          <div className="p-field mb-3">
            <label htmlFor="youtube">YouTube Video Link (Optional)</label>
            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-youtube"></i>
              </span>
              <InputText
                id="youtube"
                value={youtubeUrl}
                onChange={handleYoutubeUrlChange}
                placeholder="Enter YouTube video URL"
              />
            </div>
            {youtubeUrl && !validateYoutubeUrl(youtubeUrl) && (
              <small className="p-error">Please enter a valid YouTube URL</small>
            )}
          </div>

          <div className="p-field-checkbox mb-3">
            <Checkbox
              inputId="showInHome"
              checked={showInHome}
              onChange={(e: CheckboxChangeEvent) => {
                if (typeof e.checked === "undefined") {
                  setShowInHome(false);
                } else {
                  setShowInHome(e.checked);
                }
              }}
            />
            <label htmlFor="showInHome" className="p-checkbox-label">
              Show in Home (limited to 3 posts)
            </label>
          </div>

          <div className="p-field-checkbox mb-3">
            <Checkbox
              inputId="isSliding"
              checked={isSliding}
              onChange={(e: CheckboxChangeEvent) => {
                if (typeof e.checked === "undefined") {
                  setIsSliding(false);
                } else {
                  setIsSliding(e.checked);
                }
              }}
            />
            <label htmlFor="isSliding" className="p-checkbox-label">
              Show in Sliding Section
            </label>
          </div>

          <div className="button-container">
            <Button
              type="submit"
              label={editMode ? "Update Post" : "Create Post"}
              icon="pi pi-check"
              className="p-button-success"
              disabled={loading || (youtubeUrl ? !validateYoutubeUrl(youtubeUrl) : false)}
            />
            {editMode && (
              <Button
                type="button"
                label="Delete"
                icon="pi pi-trash"
                onClick={() => handleDelete(selectedPostId as string)}
                className="p-button-danger ml-2"
              />
            )}
          </div>
        </form>
      </Card>
      <ConfirmDialog />
    </div>
  );
};

export default AddBlog;
